import type { Dispatch, SetStateAction } from "react";
import React, { useEffect, useState } from "react";
import { MultiSelect, Switch, Panel, Box, useI18nContext, Token } from "@procore/core-react";
import type { ganttFilterOptionTypes, ganttFilterType } from "./prop-types";
import { GanttViewMode } from "./prop-types";
import { INITIAL_GANTT_FILTER } from "./gantt-constants";
import "./gantt-container.css";
import { GanttCustomFilter } from "./custom_filters/gantt-custom-filter";
import { useGroupContext } from "@/react/providers/group-context-provider";
import {
   useGetJobTitles,
   useGetProjects,
   useGetProjectStages,
   useGetTagOptionsQuery,
} from "../common/queries/queries";
import type { TagOption } from "@/react/prop-types";
import type { OptionItem } from "../data-table/custom-filters/multiselect-pill-filter";
import MultiSelectPillFilter from "../data-table/custom-filters/multiselect-pill-filter";
import { ProjectRolesFilter } from "../data-table/custom-filters/project-roles-filter";
import { projectStatuses } from "./gantt-utils";

type GanttFilterPanelProps = {
   ganttFilter: ganttFilterType;
   setGanttFilter: Dispatch<SetStateAction<ganttFilterType>>;
   onClose: () => void;
   customFilterFields?: any;
   disabled: boolean;
   isOpen: boolean;
   ganttViewMode: GanttViewMode;
};

export const GanttFilterPanel = ({
   ganttFilter,
   disabled,
   setGanttFilter,
   onClose,
   customFilterFields,
   isOpen,
   ganttViewMode,
}: GanttFilterPanelProps) => {
   const I18n = useI18nContext();
   const { groupId } = useGroupContext();
   const { data: jobTitles } = useGetJobTitles(groupId);
   const { data: projects } = useGetProjects(groupId);
   const { data: projectStages } = useGetProjectStages();
   const showStagesFilter = projectStages && projectStages.length > 0;

   const handleFilterChange = (value: any, filterType: ganttFilterOptionTypes): void => {
      setGanttFilter((ganttFilter) => ({
         ...ganttFilter,
         [filterType]: value,
      }));
   };

   const { data: tagsData, isLoading: areTagsLoading } = useGetTagOptionsQuery();
   const [projectTagOptions, setProjectTagOptions] = useState<TagOption[] | null>(null);

   useEffect(() => {
      if (areTagsLoading) return;

      setProjectTagOptions(tagsData ?? null);
   }, [areTagsLoading]);

   const clearAllFilters = (): void => {
      setGanttFilter(INITIAL_GANTT_FILTER);
   };

   return (
      <div className={isOpen ? "filterPanel visible" : "filterPanel"}>
         <Panel
            style={{
               border: "1px solid #e0e0e0",
               width: "500px",
               maxHeight: "76vh",
            }}
         >
            <Panel.Header onClose={onClose}>
               <Panel.Title>
                  {I18n.t("views.company.workforce_planning.gantt.filter.filters")}
               </Panel.Title>
               <Panel.HeaderAction title={"Reset"} onClick={clearAllFilters}>
                  <Panel.Title style={{ fontSize: "14px" }}>
                     {I18n.t("views.company.workforce_planning.gantt.filter.clear_all_filters")}
                  </Panel.Title>
               </Panel.HeaderAction>
            </Panel.Header>
            <Panel.Body>
               <Box style={{ pointerEvents: disabled ? "none" : "unset", overflowY: "scroll" }}>
                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {I18n.t("views.company.workforce_planning.gantt.filter.project")}
                     </h3>
                     <MultiSelect
                        getLabel={(project) => project.label}
                        options={projects ?? []}
                        tokenRenderer={TokenRenderer}
                        value={ganttFilter.projects}
                        onChange={(value) => handleFilterChange(value, "projects")}
                        className="ganttFilterMultiselect"
                     />
                  </div>

                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {ganttViewMode === GanttViewMode.PROJECTS
                           ? I18n.t("views.company.workforce_planning.project_tags")
                           : I18n.t("views.company.workforce_planning.people_tags")}
                     </h3>
                     <GanttMultiSelectPillFilter
                        projectTagOptions={projectTagOptions}
                        ganttFilter={ganttFilter}
                        handleFilterChange={handleFilterChange}
                     />
                  </div>
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t("views.company.workforce_planning.gantt.filter.project_status")}
                        </h3>
                        <MultiSelect
                           getLabel={(status) => status.name}
                           options={projectStatuses}
                           tokenRenderer={TokenRenderer}
                           value={ganttFilter.projectStatuses}
                           onChange={(value) => handleFilterChange(value, "projectStatuses")}
                           className="ganttFilterMultiselect"
                        />
                     </div>
                  )}
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t("views.company.workforce_planning.gantt.filter.project_roles", {
                              defaultValue: "Project Roles",
                           })}
                        </h3>
                        <ProjectRolesFilter
                           value={ganttFilter.projectRoles}
                           onChange={(value) => handleFilterChange(value, "projectRoles")}
                        />
                     </div>
                  )}
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t("views.company.workforce_planning.gantt.filter.resource_status")}
                        </h3>
                        <MultiSelect
                           getLabel={(status) => status.name}
                           options={projectStatuses.filter((status) => {
                              // filter out the 'Pending' status
                              return status.id !== 2;
                           })}
                           tokenRenderer={TokenRenderer}
                           value={ganttFilter.resourceStatuses}
                           onChange={(value) => handleFilterChange(value, "resourceStatuses")}
                           className="ganttFilterMultiselect"
                        />
                     </div>
                  )}
                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {I18n.t("views.company.workforce_planning.gantt.filter.job_title")}
                     </h3>
                     <MultiSelect
                        getLabel={(jobTitle) => jobTitle.label}
                        options={jobTitles ?? []}
                        tokenRenderer={TokenRenderer}
                        value={ganttFilter.jobTitles}
                        onChange={(value) => handleFilterChange(value, "jobTitles")}
                        className="ganttFilterMultiselect"
                     />
                  </div>
                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {I18n.t(
                           "views.company.workforce_planning.gantt.filter.hide_past_assignments_and_requests",
                        )}
                     </h3>
                     <Switch
                        checked={ganttFilter.hidePast}
                        onChange={() => handleFilterChange(!ganttFilter.hidePast, "hidePast")}
                     />
                  </div>
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t(
                              "views.company.workforce_planning.gantt.filter.hide_empty_projects",
                           )}
                        </h3>
                        <Switch
                           checked={ganttFilter.hideEmptyProject}
                           onChange={() =>
                              handleFilterChange(!ganttFilter.hideEmptyProject, "hideEmptyProject")
                           }
                        />
                     </div>
                  )}
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t(
                              "views.company.workforce_planning.gantt.filter.hide_empty_categories",
                           )}
                        </h3>
                        <Switch
                           checked={ganttFilter.hideEmptyCategories}
                           onChange={() =>
                              handleFilterChange(
                                 !ganttFilter.hideEmptyCategories,
                                 "hideEmptyCategories",
                              )
                           }
                        />
                     </div>
                  )}
                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {I18n.t("views.company.workforce_planning.gantt.filter.hide_labor")}
                     </h3>
                     <Switch
                        checked={ganttFilter.hideTypeLabor}
                        onChange={() =>
                           handleFilterChange(!ganttFilter.hideTypeLabor, "hideTypeLabor")
                        }
                     />
                  </div>
                  <div className="ganttFilterLabel">
                     <h3 className="filterName">
                        {I18n.t("views.company.workforce_planning.gantt.filter.hide_equipment")}
                     </h3>
                     <Switch
                        checked={ganttFilter.hideTypeEquipment}
                        onChange={() =>
                           handleFilterChange(!ganttFilter.hideTypeEquipment, "hideTypeEquipment")
                        }
                     />
                  </div>
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t(
                              "views.company.workforce_planning.gantt.filter.hide_assignments",
                           )}
                        </h3>
                        <Switch
                           checked={ganttFilter.hideAssignments}
                           onChange={() =>
                              handleFilterChange(!ganttFilter.hideAssignments, "hideAssignments")
                           }
                        />
                     </div>
                  )}
                  {ganttViewMode === GanttViewMode.PROJECTS && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t("views.company.workforce_planning.gantt.filter.hide_requests")}
                        </h3>
                        <Switch
                           checked={ganttFilter.hideRequests}
                           onChange={() =>
                              handleFilterChange(!ganttFilter.hideRequests, "hideRequests")
                           }
                        />
                     </div>
                  )}
                  {showStagesFilter && (
                     <div className="ganttFilterLabel">
                        <h3 className="filterName">
                           {I18n.t("views.company.workforce_planning.projects.cornerstone_stage")}
                        </h3>
                        <MultiSelect
                           getLabel={(projectStage) => projectStage.label}
                           options={projectStages ?? []}
                           tokenRenderer={TokenRenderer}
                           value={ganttFilter.projectStages}
                           onChange={(value) => handleFilterChange(value, "projectStages")}
                           className="ganttFilterMultiselect"
                        />
                     </div>
                  )}
                  {customFilterFields?.projects_custom_field_filters.map((filter: any) => {
                     if (filter) {
                        return (
                           <GanttCustomFilter
                              filter={filter}
                              setGanttFilter={setGanttFilter}
                              ganttFilter={ganttFilter}
                              key={filter.id}
                           />
                        );
                     }
                  })}
               </Box>
            </Panel.Body>
         </Panel>
      </div>
   );
};

const GanttMultiSelectPillFilter = ({
   projectTagOptions,
   ganttFilter,
   handleFilterChange,
}: {
   projectTagOptions: TagOption[] | null;
   ganttFilter: ganttFilterType;
   handleFilterChange: (value: any, filterType: ganttFilterOptionTypes) => void;
}) => {
   const tagOptionToOptionItem = (tagOption: TagOption): OptionItem => ({
      id: tagOption.id,
      label: tagOption.label,
      name: tagOption.name,
      color: tagOption.color,
      componentName: "GanttFilterPanel",
      value: tagOption.id,
   });

   return (
      <MultiSelectPillFilter
         onChange={(value) => handleFilterChange(value, "projectTags")}
         value={ganttFilter.projectTags}
         explicitOptions={projectTagOptions?.map(tagOptionToOptionItem) ?? []}
      />
   );
};

export const TokenRenderer = ({ option, removeToken }: any) => (
   <Token
      style={{
         margin: "0 4px 0 -2px",
         padding: "0",
      }}
   >
      <Token.Label
         style={{
            paddingLeft: "8px",
         }}
      >
         {option.name ?? option.label}
      </Token.Label>
      <Token.Remove
         onClick={removeToken}
         style={{
            marginRight: "1px",
         }}
      />
   </Token>
);
